import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, AlertTriangle, Wrench } from "lucide-react";
import api from "@/lib/api";

const fontHead = { fontFamily: "'Barlow Condensed', system-ui, sans-serif" };

function Row({ v }) {
  const overdue = v.status === "overdue";
  return (
    <Link
      to="/app/fleet"
      className="flex items-center gap-3 px-3 py-2.5 border-b border-zinc-200 dark:border-zinc-800 hover:bg-zinc-50 dark:hover:bg-[#151C27] last:border-b-0 transition-colors"
      data-testid={`service-due-row-${v.id}`}
    >
      <Wrench className="w-4 h-4 shrink-0 text-zinc-400 dark:text-zinc-500" />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span
            className="text-[13px] tabular-nums text-zinc-900 dark:text-white truncate"
            style={{ ...fontHead, fontWeight: 800, letterSpacing: "0.04em" }}
          >
            {v.rego}
          </span>
          <span
            className="text-[10px] uppercase tracking-[0.1em] text-zinc-500 dark:text-zinc-400 truncate"
            style={{ ...fontHead, fontWeight: 700 }}
          >
            · {v.make} {v.model}
          </span>
        </div>
        <div
          className="text-[10px] uppercase tracking-[0.1em] mt-0.5 text-zinc-500 dark:text-zinc-400"
          style={{ ...fontHead, fontWeight: 700 }}
        >
          {v.vehicle_type || "Plant"} · {v.current_meter?.toFixed(0)}h on clock
        </div>
      </div>
      <span
        className={`inline-flex items-center px-1.5 py-0.5 text-[9px] uppercase tracking-[0.16em] rounded-sm whitespace-nowrap ${
          overdue ? "bg-red-600 text-white" : "bg-amber-500 text-zinc-900"
        }`}
        style={{ ...fontHead, fontWeight: 700 }}
      >
        {v.reason || (overdue ? "Overdue" : "Due soon")}
      </span>
      <span
        className="text-[9px] uppercase tracking-[0.16em] px-2 py-1 rounded-sm bg-zinc-100 dark:bg-[#151C27] text-zinc-700 dark:text-zinc-200 shrink-0"
        style={{ ...fontHead, fontWeight: 700 }}
      >
        Fix →
      </span>
    </Link>
  );
}

export default function PlantServiceDueWidget() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api
      .get("/fleet/service-due")
      .then((r) => mounted && setItems(r.data || []))
      .catch(() => mounted && setItems([]))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  if (loading) {
    return (
      <div
        className="rounded-sm border border-zinc-300 dark:border-zinc-800 bg-white dark:bg-[#101620] p-4"
        data-testid="plant-service-loading"
      >
        <div className="h-4 w-40 bg-zinc-100 dark:bg-[#151C27] animate-pulse" />
      </div>
    );
  }

  const overdue = items.filter((i) => i.status === "overdue").length;
  const dueSoon = items.filter((i) => i.status === "due_soon").length;

  if (items.length === 0) {
    return (
      <div
        className="rounded-sm border border-zinc-300 dark:border-zinc-800 bg-white dark:bg-[#101620] p-4"
        data-testid="plant-service-clear"
      >
        <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
          <Activity className="w-3.5 h-3.5" />
          <span
            className="text-[10px] uppercase tracking-[0.22em]"
            style={{ ...fontHead, fontWeight: 700 }}
          >
            // Plant service · on track
          </span>
        </div>
        <p className="text-[11px] text-zinc-600 dark:text-zinc-400 mt-1">
          No machines flagged for service. Add a service interval to a vehicle to start tracking.
        </p>
      </div>
    );
  }

  return (
    <div
      className="rounded-sm border border-zinc-300 dark:border-zinc-800 bg-white dark:bg-[#101620] overflow-hidden"
      data-testid="plant-service-widget"
    >
      <div className="px-5 py-3 border-b border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-[#151C27] flex items-center gap-2">
        <Wrench className="w-3.5 h-3.5 text-amber-500" />
        <span
          className="text-[10px] uppercase tracking-[0.22em] text-amber-500"
          style={{ ...fontHead, fontWeight: 700 }}
        >
          // Plant service due
        </span>
        <span className="ml-auto flex items-center gap-1.5">
          {overdue > 0 && (
            <span
              className="inline-flex items-center px-1.5 py-0.5 text-[9px] uppercase tracking-[0.16em] rounded-sm bg-red-600 text-white"
              style={{ ...fontHead, fontWeight: 700 }}
            >
              <AlertTriangle className="w-3 h-3 mr-1" /> {overdue} overdue
            </span>
          )}
          {dueSoon > 0 && (
            <span
              className="inline-flex items-center px-1.5 py-0.5 text-[9px] uppercase tracking-[0.16em] rounded-sm bg-amber-500 text-zinc-900"
              style={{ ...fontHead, fontWeight: 700 }}
            >
              {dueSoon} due soon
            </span>
          )}
        </span>
      </div>
      <div>
        {items.slice(0, 10).map((v) => <Row key={v.id} v={v} />)}
      </div>
      {items.length > 10 && (
        <Link
          to="/app/fleet"
          className="block px-5 py-2.5 text-center text-[10px] uppercase tracking-[0.18em] text-[#FF5F15] hover:bg-zinc-50 dark:hover:bg-[#151C27] border-t border-zinc-200 dark:border-zinc-800"
          style={{ ...fontHead, fontWeight: 700 }}
          data-testid="plant-service-view-all"
        >
          View all {items.length} machines →
        </Link>
      )}
    </div>
  );
}
